import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import i18next from 'i18next';
import { useAuth } from './AuthContext';

interface LanguageContextProps {
    currentLanguage: string;
    changeLanguage: (language: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { user, updateUserLanguage } = useAuth();
    const [currentLanguage, setCurrentLanguage] = useState('en');

    // Initialize language from user profile and cache in localStorage
    useEffect(() => {
        console.log('LanguageContext: Initializing language...');

        // First try to get language from localStorage (persisted preference)
        const cachedLanguage = localStorage.getItem('appLanguage');
        console.log('LanguageContext: Cached language from localStorage:', cachedLanguage);

        // Then get language from user profile
        const userLanguage = user?.language;
        console.log('LanguageContext: User language from profile:', userLanguage);

        // Use cached language if available, otherwise map user language, fallback to 'en-US'
        let languageCode = cachedLanguage;

        if (!languageCode && userLanguage) {
            // Map user language name to language code
            languageCode = userLanguage || 'en-US';
            // Cache the mapped language for future use
            localStorage.setItem('appLanguage', languageCode);
            console.log('LanguageContext: Mapped and cached user language:', languageCode);
        }

        // Fallback to 'en-US' if no language is available
        if (!languageCode) {
            languageCode = 'en-US';
            console.log('LanguageContext: Using fallback language:', languageCode);
        }

        console.log('LanguageContext: Final language code:', languageCode);

        // Update current language state
        setCurrentLanguage(languageCode);

        // Change i18next language
        i18next.changeLanguage(languageCode);
    }, [user?.language]);

    const changeLanguage = async (language: string) => {
        try {
            console.log('LanguageContext: changeLanguage called with:', language);

            // Map language code back to language name for database
            const reverseLanguageMap: Record<string, string> = {
                'en-US': 'English',
                'pt-BR': 'Portuguese',
                'es-ES': 'Spanish',
                'fr-FR': 'French',
                'de-DE': 'German'
            };

            const languageName = reverseLanguageMap[language] || 'English';

            // Update user profile in database with new language
            await updateUserLanguage(languageName);

            // Cache the new language in localStorage
            localStorage.setItem('appLanguage', language);

            await i18next.changeLanguage(language);
            setCurrentLanguage(language);
            console.log('LanguageContext: Language changed successfully to:', language);
        } catch (error) {
            console.error('Error changing language:', error);
        }
    };

    return (
        <LanguageContext.Provider
            value={{
                currentLanguage,
                changeLanguage,
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextProps => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}; 